var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
// Create forwardRef component for DatePicker
import dayjs from 'dayjs';
import React, { useEffect, useState, useCallback } from 'react';
import { useUtils } from '@mui/x-date-pickers/internals';
import { TimePicker } from './TimePicker';
var getMeridiem = function (time) { return dayjs(time).format('A'); };
export var convertToMeridiem = function (time, meridiem) {
    var currentMeridiem = getMeridiem(time);
    if (currentMeridiem !== meridiem) {
        var hours = dayjs(time).hour() + (meridiem === 'AM' ? -12 : 12);
        // if hours is invalid date, then create new date from today
        return dayjs(time).hour(hours);
    }
    return time;
};
export function get24Hours(ampm, hour, period) {
    var numHour = Number(hour);
    if (!ampm) {
        return numHour;
    }
    if (period === 'AM' && numHour === 12) {
        numHour -= 12;
    }
    else if (period === 'PM' && numHour < 12) {
        numHour += 12;
    }
    return numHour;
}
export function InnerTimePicker(_a) {
    var _b, _c;
    var onChange = _a.onChange, props = __rest(_a, ["onChange"]);
    var value = props.value;
    var utils = useUtils();
    var isDayjsLib = utils.lib !== 'dayjs';
    // @ts-ignore -- if ampm is not defined, it will default to locale settings.
    var ampm = (_c = (_b = props === null || props === void 0 ? void 0 : props.slotProps) === null || _b === void 0 ? void 0 : _b.toolbar) === null || _c === void 0 ? void 0 : _c.ampm;
    if (utils.lib !== 'dayjs') {
        // This will convert time to dayjs format, no matter what adapter is used so we can work with date as it is a dayjs object.
        // We need to to this because someone can use different adapter and this component uses dayjs to simplify work with time.
        value = dayjs(utils.toJsDate(value));
    }
    // Convert value to different format if dayjs not used.
    var getRightFormat = useCallback(function (valueToConvert) {
        if (isDayjsLib) {
            return valueToConvert;
        }
        return utils.date(valueToConvert.toDate());
    }, [isDayjsLib, utils]);
    // If initial value is null or undefined, handle it in useEffect.
    var _d = useState(value == null), emptyInitialValue = _d[0], setEmptyInitialValue = _d[1];
    var _e = useState(function () { return ({
        second: dayjs(value || undefined).format('ss'),
        minute: dayjs(value || undefined).format('mm'),
        hour: dayjs(value || undefined).format(ampm ? 'hh' : 'HH'),
    }); }), time = _e[0], setTime = _e[1];
    var handleStepChange = function (type) { return function (newVal) {
        var _a;
        setTime(__assign(__assign({}, time), (_a = {}, _a[type] = newVal, _a)));
        var valueToSend = Number(newVal);
        if (ampm && type === 'hour') {
            valueToSend = get24Hours(ampm, newVal, getMeridiem(value || dayjs()));
        }
        var newDate = dayjs(value || undefined)[type](valueToSend);
        onChange(getRightFormat(newDate));
    }; };
    var handleInputChange = function (type) { return function (e) {
        var _a;
        var newValue = e.target.value.length > 2 ? Number(e.target.value.slice(-1)) : Number(e.target.value);
        setTime(__assign(__assign({}, time), (_a = {}, _a[type] = newValue, _a)));
    }; };
    var handleBlur = function (type) { return function (newVal) {
        var _a;
        setTime(__assign(__assign({}, time), (_a = {}, _a[type] = newVal, _a)));
        var valueToSend = Number(newVal);
        if (ampm && type === 'hour') {
            valueToSend = get24Hours(ampm, newVal, getMeridiem(value || dayjs()));
        }
        var newDate = dayjs(value || undefined)[type](valueToSend);
        onChange(getRightFormat(newDate));
    }; };
    var handleAmpmChange = function (newVal) {
        var calculated = convertToMeridiem(value || dayjs(), newVal);
        onChange(getRightFormat(calculated));
    };
    // to sync when value is null we need to use effect
    useEffect(function () {
        if (value == null) {
            return;
        }
        if (emptyInitialValue) {
            var updatedTime = dayjs(value)
                .hour(get24Hours(ampm, time.hour, getMeridiem(value)))
                .minute(Number(time.minute))
                .second(Number(time.second));
            setTime({
                second: updatedTime.format('ss'),
                minute: updatedTime.format('mm'),
                hour: updatedTime.format(ampm ? 'hh' : 'HH'),
            });
            onChange(getRightFormat(updatedTime));
            setEmptyInitialValue(false);
        }
    }, [value != null]);
    return (React.createElement(TimePicker, { ampmValue: getMeridiem(dayjs(value || undefined)), autoFocus: false, hoursValue: String(time.hour), isAmPm: ampm, minutesStep: (props === null || props === void 0 ? void 0 : props.minutesStep) || 1, secondsStep: (props === null || props === void 0 ? void 0 : props.secondsStep) || 1, minutesValue: String(time.minute), onAmpmChange: ampm ? handleAmpmChange : undefined, onHoursBlur: handleBlur('hour'), onHoursChange: handleInputChange('hour'), onHoursStepChange: handleStepChange('hour'), onMinutesBlur: handleBlur('minute'), onMinutesChange: handleInputChange('minute'), onMinutesStepChange: handleStepChange('minute'), onSecondsBlur: handleBlur('second'), onSecondsChange: handleInputChange('second'), onSecondsStepChange: handleStepChange('second'), secondsValue: String(time.second), showSeconds: props.views.includes('seconds') || props.showSeconds }));
}
